import axios from 'axios';
import {wasDefinitelyRefused} from '@/utils/apiOutcome';
import {describeCloudFunctionOutcome} from '@/utils/cloudFunction';
import {
    describeAccessChangeOutcome,
    describeClientUsersOutcome,
    describeReinvitationOutcome,
} from '@/utils/clientUserOverview';
import {
    buildClientUserPayload,
    buildPortalUserPayload,
    describeOnboardingOutcome,
    describePortalAccountOutcome,
    generateNeverDisclosedPassword,
} from '@/utils/clientUserOnboarding';

const ORPHANED_PORTAL_ACCOUNT = 'Het portaalaccount is aangemaakt maar kon niet worden gekoppeld of verwijderd. Verwijder het handmatig bij Gebruikers voordat je het opnieuw probeert.';

const UNCERTAIN_ONBOARDING = 'De urenapp gaf geen antwoord, dus het is onbekend of de klantgebruiker is aangemaakt. Controleer het bij Gebruikers voordat je het opnieuw probeert.';

const clientUserUrl = clientUser => process.env.VUE_APP_HOUR_APP_BACKEND + '/api/client-user/' + encodeURIComponent(clientUser.id);

const deactivationUrl = clientUser => clientUserUrl(clientUser) + '/deactivation';

const actions = {
    fetchForOrganization: async (context, payload) => {
        const ApiClient = payload.ApiClient;

        return describeClientUsersOutcome(await axios.get(
            process.env.VUE_APP_HOUR_APP_BACKEND + '/api/organization/' + encodeURIComponent(payload.organizationId) + '/client-users',
            {params: {firebaseIdToken: ApiClient.token}},
        ).then(res => res).catch(err => err));
    },
    deactivate: async (context, payload) => {
        const ApiClient = payload.ApiClient;

        return describeAccessChangeOutcome(await axios.post(deactivationUrl(payload.clientUser), {
            firebaseIdToken: ApiClient.token,
        }).then(res => res).catch(err => err));
    },
    reactivate: async (context, payload) => {
        const ApiClient = payload.ApiClient;

        return describeAccessChangeOutcome(await axios.delete(deactivationUrl(payload.clientUser), {
            params: {firebaseIdToken: ApiClient.token},
        }).then(res => res).catch(err => err));
    },
    resendInvitation: async (context, payload) => {
        const ApiClient = payload.ApiClient;

        return describeReinvitationOutcome(await axios.post(clientUserUrl(payload.clientUser) + '/invitation', {
            firebaseIdToken: ApiClient.token,
        }).then(res => res).catch(err => err));
    },
    checkEmailAvailability: (context, payload) => {
        const ApiClient = payload.ApiClient;

        return axios.get(process.env.VUE_APP_HOUR_APP_BACKEND + '/api/client-user/availability', {
            params: {
                firebaseIdToken: ApiClient.token,
                email: payload.email,
                orgFirebaseId: payload.customerId,
            },
        }).then(res => res).catch(err => err);
    },
    onboard: (context, payload) => {
        const ApiClient = payload.ApiClient;

        return axios.post(process.env.VUE_APP_HOUR_APP_BACKEND + '/api/client-user', {
            ...payload.clientUser,
            firebaseIdToken: ApiClient.token,
        }).then(res => res).catch(err => err);
    },
    fetchInvitation: (context, payload) => {
        return axios.get(process.env.VUE_APP_HOUR_APP_BACKEND + '/api/client-user/invitation/' + encodeURIComponent(payload.token))
            .then(res => res)
            .catch(err => err);
    },
    setInvitationPassword: (context, payload) => {
        return axios.post(
            process.env.VUE_APP_HOUR_APP_BACKEND + '/api/client-user/invitation/' + encodeURIComponent(payload.token) + '/password',
            {
                password: payload.password,
                password_confirmation: payload.passwordConfirmation,
            },
        ).then(res => res).catch(err => err);
    },
    onboardWithPortalAccount: async ({dispatch}, payload) => {
        const ApiClient = payload.ApiClient;
        const {form, customer, departmentName, roleId} = payload;

        const portalAccount = describePortalAccountOutcome(await dispatch('users/postUser', {
            ApiClient,
            user: buildPortalUserPayload({
                form,
                roleId,
                departmentName,
                password: generateNeverDisclosedPassword(),
            }),
        }, {root: true}));

        if (!portalAccount.ok) {
            return portalAccount;
        }

        const invited = describeOnboardingOutcome(await dispatch('onboard', {
            ApiClient,
            clientUser: buildClientUserPayload({form, firebaseId: portalAccount.firebaseId, customer}),
        }));

        if (invited.ok) {
            return {ok: true, emailError: null, message: null};
        }

        if (!wasDefinitelyRefused(invited.status)) {
            return {ok: false, emailError: null, message: UNCERTAIN_ONBOARDING};
        }

        const withdrawn = describeCloudFunctionOutcome(await dispatch('users/deleteUser', {
            ApiClient,
            user: {uid: portalAccount.firebaseId, departments: [{name: departmentName}]},
        }, {root: true}));

        if (!withdrawn.ok) {
            return {ok: false, emailError: null, message: ORPHANED_PORTAL_ACCOUNT};
        }

        return {ok: false, emailError: invited.fieldError, message: invited.message};
    },
};

export default actions;
